import type { KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './components/PageHeader';

export default function InternalDashboard({ currentUser }: { currentUser?: any }) {
    const navigate = useNavigate();
    const isSuperUser = currentUser?.isSuperUser === true;
    const internalActions = currentUser?.internalRoleActions || [];
    const hasInternalAction = (action: string) => internalActions.includes(action);
    const previewLayoutsSubtitle = hasInternalAction('WriteInternalPreviewLayouts')
        ? 'View and create preview layouts.'
        : 'View preview layouts.';

    function navigateOnEnter(event: KeyboardEvent<HTMLDivElement>, path: string) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            navigate(path);
        }
    }

    return (
        <div className="stack" data-testid="page-internal-dashboard">
            <PageHeader
                title="Internal Administration"
                subtitle="Internal tools and management"
            />

            <div className="two-col admin-overview-grid">
              {[currentUser?.internalRoleActions, currentUser?.roleActions, currentUser?.permissions?.actions]
                .flatMap((actions) => Array.isArray(actions) ? actions : []).includes('ReadInternalReports') && (
                <div className="card admin-overview-card" role="link" tabIndex={0}
                  onClick={() => navigate('/internal/pulse')}
                  onKeyDown={(event) => navigateOnEnter(event, '/internal/pulse')}
                  data-testid="internal-nav-pulse">
                  <div className="admin-overview-card-header"><div className="admin-overview-card-titles">
                    <div className="admin-overview-card-title">RID Pulse</div>
                    <div className="muted">Monitor five-minute operational activity.</div>
                  </div></div>
                </div>
              )}
              {isSuperUser && (
                <>
                    {hasInternalAction('AdminInternalMergeQuickstartOrganizations') && (
                    <div
                        className="card admin-overview-card"
                        role="link"
                        tabIndex={0}
                        onClick={() => navigate('/internal/organizations/merge')}
                        onKeyDown={(event) => navigateOnEnter(event, '/internal/organizations/merge')}
                        data-testid="internal-merge-organizations-link"
                    >
                        <div className="admin-overview-card-header">
                            <div className="admin-overview-card-titles">
                                <div className="admin-overview-card-title">Merge Quickstart Organizations</div>
                            </div>
                        </div>
                    </div>
                    )}

                    {hasInternalAction('ReadInternalManualUserVerification') && (
                    <div
                        className="card admin-overview-card"
                        role="link"
                        tabIndex={0}
                        onClick={() => navigate('/internal/users/user-management')}
                        onKeyDown={(event) => navigateOnEnter(event, '/internal/users/user-management')}
                        data-testid="internal-user-management-link"
                    >
                        <div className="admin-overview-card-header">
                            <div className="admin-overview-card-titles">
                                <div className="admin-overview-card-title">User Management</div>
                            </div>
                        </div>
                    </div>
                    )}

                    {hasInternalAction('AdminInternalMergeQuickstartOrganizations') && (
                    <div
                        className="card admin-overview-card"
                        role="link"
                        tabIndex={0}
                        onClick={() => navigate('/internal/organizations/merge-organizations')}
                        onKeyDown={(event) => navigateOnEnter(event, '/internal/organizations/merge-organizations')}
                        data-testid="internal-merge-standard-organizations-link"
                    >
                        <div className="admin-overview-card-header">
                            <div className="admin-overview-card-titles">
                                <div className="admin-overview-card-title">Merge Organizations</div>
                            </div>
                        </div>
                    </div>
                    )}

                    {hasInternalAction('AdminInternalSuperuserManagement') && (
                    <div
                        className="card admin-overview-card"
                        role="link"
                        tabIndex={0}
                        onClick={() => navigate('/internal/users/super-users')}
                        onKeyDown={(event) => navigateOnEnter(event, '/internal/users/super-users')}
                        data-testid="internal-nav-super-users"
                    >
                        <div className="admin-overview-card-header">
                            <div className="admin-overview-card-titles">
                                <div className="admin-overview-card-title">Manage Super Users</div>
                                <div className="muted">Promote or demote RID super users by email.</div>
                            </div>
                        </div>
                    </div>
                    )}

                    {hasInternalAction('ReadInternalPackageSummary') && (
                    <div
                        className="card admin-overview-card"
                        role="link"
                        tabIndex={0}
                        onClick={() => navigate('/internal/package-summary')}
                        onKeyDown={(event) => navigateOnEnter(event, '/internal/package-summary')}
                        data-testid="internal-nav-package-summary"
                    >
                        <div className="admin-overview-card-header">
                            <div className="admin-overview-card-titles">
                                <div className="admin-overview-card-title">Package Summary</div>
                                <div className="muted">Look up a package summary by label.</div>
                            </div>
                        </div>
                    </div>
                    )}

                    {hasInternalAction('ReadInternalPreviewLayouts') && (
                    <div
                        className="card admin-overview-card"
                        role="link"
                        tabIndex={0}
                        onClick={() => navigate('/internal/preview-layouts')}
                        onKeyDown={(event) => navigateOnEnter(event, '/internal/preview-layouts')}
                        data-testid="internal-nav-preview-layouts"
                    >
                        <div className="admin-overview-card-header">
                            <div className="admin-overview-card-titles">
                                <div className="admin-overview-card-title">Preview Layouts</div>
                                <div className="muted">{previewLayoutsSubtitle}</div>
                            </div>
                        </div>
                    </div>
                    )}

                    {hasInternalAction('WriteInternalStagedPackagePromotion') && (
                    <div
                        className="card admin-overview-card"
                        role="link"
                        tabIndex={0}
                        onClick={() => navigate('/internal/package-promotion')}
                        onKeyDown={(event) => navigateOnEnter(event, '/internal/package-promotion')}
                        data-testid="internal-nav-package-promotion"
                    >
                        <div className="admin-overview-card-header">
                            <div className="admin-overview-card-titles">
                                <div className="admin-overview-card-title">Promote Staged Packages</div>
                                <div className="muted">Find and manually promote staged packages.</div>
                            </div>
                        </div>
                    </div>
                    )}

                    {hasInternalAction('ReadInternalCoaVisibility') && (
                    <div
                        className="card admin-overview-card"
                        role="link"
                        tabIndex={0}
                        onClick={() => navigate('/internal/coa-visibility')}
                        onKeyDown={(event) => navigateOnEnter(event, '/internal/coa-visibility')}
                        data-testid="internal-nav-coa-visibility"
                    >
                        <div className="admin-overview-card-header">
                            <div className="admin-overview-card-titles">
                                <div className="admin-overview-card-title">COA Visibility</div>
                                <div className="muted">Hide or show COA files on package landing pages.</div>
                            </div>
                        </div>
                    </div>
                    )}

                    {hasInternalAction('ReadInternalStagedPackageSummary') && (
                    <div
                        className="card admin-overview-card"
                        role="link"
                        tabIndex={0}
                        onClick={() => navigate('/internal/staged-package-summary')}
                        onKeyDown={(event) => navigateOnEnter(event, '/internal/staged-package-summary')}
                        data-testid="internal-nav-staged-package-summary"
                    >
                        <div className="admin-overview-card-header">
                            <div className="admin-overview-card-titles">
                                <div className="admin-overview-card-title">Staged Package Summary</div>
                                <div className="muted">Look up a staged package summary by label.</div>
                            </div>
                        </div>
                    </div>
                    )}

                    {hasInternalAction('ReadInternalFacilitySearch') && (
                    <div
                        className="card admin-overview-card"
                        role="link"
                        tabIndex={0}
                        onClick={() => navigate('/internal/facility-search')}
                        onKeyDown={(event) => navigateOnEnter(event, '/internal/facility-search')}
                        data-testid="internal-nav-facility-search"
                    >
                        <div className="admin-overview-card-header">
                            <div className="admin-overview-card-titles">
                                <div className="admin-overview-card-title">Facility Search</div>
                                <div className="muted">Search for facilities by license number.</div>
                            </div>
                        </div>
                    </div>
                    )}

                {hasInternalAction('ReadInternalReports') && (
                  <div
                    className="card admin-overview-card"
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate('/internal/reports')}
                    onKeyDown={(event) => navigateOnEnter(event, '/internal/reports')}
                    data-testid="internal-nav-reports"
                  >
                    <div className="admin-overview-card-header">
                      <div className="admin-overview-card-titles">
                        <div className="admin-overview-card-title">Reports</div>
                        <div className="muted">View various internal reports.</div>
                      </div>
                    </div>
                  </div>
                )}

                {hasInternalAction('ReadInternalUserRoles') && (
                  <div
                    className="card admin-overview-card"
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate('/internal/users/administration')}
                    onKeyDown={(event) => navigateOnEnter(event, '/internal/users/administration')}
                    data-testid="internal-nav-roles"
                  >
                    <div className="admin-overview-card-header">
                      <div className="admin-overview-card-titles">
                        <div className="admin-overview-card-title">User Administration</div>
                        <div className="muted">Manage internal user roles.</div>
                      </div>
                    </div>
                  </div>
                )}
                    {hasInternalAction('ManageInternalLandingPages') && (
                    <div
                        className="card admin-overview-card"
                        role="link"
                        tabIndex={0}
                        onClick={() => navigate('/internal/landing-pages')}
                        onKeyDown={(event) => navigateOnEnter(event, '/internal/landing-pages')}
                        data-testid="internal-nav-landing-pages"
                    >
                        <div className="admin-overview-card-header">
                            <div className="admin-overview-card-titles">
                                <div className="admin-overview-card-title">Landing Pages</div>
                            </div>
                        </div>
                    </div>
                    )}
                </>
              )}
            </div>
        </div>
    );
}
